import React from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { notAvailableYetHint } from '@/lib/notAvailableYet';

// Pen icon used by every editable setting (REQ-024). When the backend cannot
// store the value yet, it is shown disabled with the use case that unblocks it.

interface EditButtonProps {
  label: string;
  onClick?: () => void;
  /** Backend use case that blocks editing, e.g. "UC-214". */
  unavailableUseCase?: string;
}

export const EditButton: React.FC<EditButtonProps> = ({ label, onClick, unavailableUseCase }) => {
  const tooltip = unavailableUseCase ? `${label}. ${notAvailableYetHint(unavailableUseCase)}` : label;
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={tooltip}
      onClick={onClick}
      disabled={!!unavailableUseCase}
      className={unavailableUseCase ? 'pointer-events-none' : undefined}
    >
      <Pencil className="h-4 w-4" aria-hidden="true" />
    </Button>
  );
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A disabled button gets no pointer events, so the tooltip sits on a wrapper. */}
          {unavailableUseCase ? <span tabIndex={0} className="inline-flex">{button}</span> : button}
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EditButton;
